import { APIResponseType } from "@/shared";

import { createGetApiHook } from "@/shared/api/apiHooks";

/* -------------------------------------------------------------------------- */
/* Verification response types                                                */
/* -------------------------------------------------------------------------- */

export type VerifyPurchaseContent = {
  id: number;

  talent_id: number;

  title: string;

  description?: string | null;

  thumbnail?: string | null;

  created_at: string;

  updated_at: string;
};

export type VerifyPurchaseMainCard = {
  id: number;

  cover_image_url?: string | null;

  type: string;

  ref_id: number;

  status: string;

  limited_edition?: number | null;

  created_at: string;

  content: VerifyPurchaseContent;
};

export type VerifyPurchaseUser = {
  id: number;

  first_name?: string | null;

  last_name?: string | null;

  username: string;

  profile_image?: string | null;
};

export type VerifyPurchaseTalent = {
  id: number;

  username: string;

  profile_image?: string | null;

  cover_image?: string | null;

  type?: string | null;
};

export type VerifyPurchaseData = {
  /**
   * Purchase ID.
   */
  id: number;

  /**
   * Exact edition owned by this purchase.
   */
  edition?: number | null;

  /**
   * Purchase timestamp.
   */
  created_at: string;

  /**
   * Purchased main card.
   */
  main_card: VerifyPurchaseMainCard;

  /**
   * Current owner.
   */
  user: VerifyPurchaseUser;

  /**
   * Issuer / talent.
   */
  talent: VerifyPurchaseTalent;
};

/* -------------------------------------------------------------------------- */
/* Verify parameters                                                          */
/* -------------------------------------------------------------------------- */

export type VerifyPurchaseParams = {
  purchaseId: number | string;
};

/* -------------------------------------------------------------------------- */
/* Verify endpoint                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Example:
 *
 * GET /purchase/230/verify
 *
 * purchaseId = 230
 */
const verifyPurchaseAPI = ({ purchaseId }: VerifyPurchaseParams) =>
  `main-card/purchase/${encodeURIComponent(String(purchaseId))}/verify`;

/* -------------------------------------------------------------------------- */
/* Verify hook                                                                */
/* -------------------------------------------------------------------------- */

export const useVerifyPurchase = createGetApiHook<
  APIResponseType<VerifyPurchaseData>,
  VerifyPurchaseParams
>(({ purchaseId }) => verifyPurchaseAPI({ purchaseId }));
